/**
 * FocalCard router state. The chat brain + user clicks both push state here.
 * The FocalCard component renders the matching view.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type FocalState =
  | { kind: 'chart'; symbol: string }
  | { kind: 'order'; symbol: string }
  | { kind: 'agent'; pane_id: string }
  | { kind: 'backtest' }
  | { kind: 'positions' }
  | { kind: 'orders' }
  | { kind: 'pending' }
  | { kind: 'strategies' }
  | { kind: 'research'; query?: string }
  | { kind: 'profile' }
  | { kind: 'settings' }
  // v0.5.1 Morning Coffee
  | { kind: 'morning_brief' }
  | { kind: 'portfolio_board' }
  | { kind: 'playbook' }
  | { kind: 'autonomy' }
  | { kind: 'theses' }
  | { kind: 'thesis_wizard'; wizardId?: string }
  | { kind: 'thesis_detail'; thesisId: string };

interface FocalCtx {
  state: FocalState | null;
  set: (s: FocalState | null) => void;
}

const Ctx = createContext<FocalCtx | null>(null);

export function FocalProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<FocalState | null>(null);
  const set = useCallback((s: FocalState | null) => setState(s), []);
  const value = useMemo(() => ({ state, set }), [state, set]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// No-op stub used when a component that uses useFocal is rendered outside
// the FocalProvider (e.g. isolated page-level unit tests). The deeplink
// useEffect just no-ops; the actual FocalCard never sees these states.
const FOCAL_STUB: FocalCtx = {
  state: null,
  set: () => {},
};

export function useFocal() {
  return useContext(Ctx) ?? FOCAL_STUB;
}
